import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, type DragEvent, type KeyboardEvent, type PointerEvent } from "react";
import { ChevronLeft, ChevronRight, File, Folder } from "lucide-react";
import type { DesktopAppDefinition } from "../desktop/apps";
import type { HostEntry } from "../lib/hostWorkspace";
import type { WorkArea } from "../desktop/windowGeometry";
import { encodeSlot, iconGrid, moveIcon, readIconPositions, resolveIconSlots, slotAt, writeIconPositions, type IconPositions, type IconSlot } from "../desktop/iconLayout";
import { DesktopAppIcon } from "./DesktopAppIcon";

export type DesktopShortcutsHandle = { organize: () => void; reset: () => void };
type Props = {
  apps: DesktopAppDefinition[]; entries: HostEntry[]; workArea: WorkArea; userId: string;
  density?: "comfortable" | "compact";
  onLaunch: (app: DesktopAppDefinition) => void;
  onOpenEntry: (entry: HostEntry) => void;
  onPrepareExport: (entry: HostEntry) => void;
  onExport: (event: DragEvent<HTMLButtonElement>, entry: HostEntry) => void;
};
type Shortcut = { id: string; title: string; app?: DesktopAppDefinition; entry?: HostEntry };
type Gesture = { id: string; pointerId: number; x: number; y: number; slot: IconSlot; target: IconSlot; moved: boolean; element: HTMLButtonElement };

export const DesktopShortcuts = forwardRef<DesktopShortcutsHandle, Props>(function DesktopShortcuts({ apps, entries, workArea, userId, density = "comfortable", onLaunch, onOpenEntry, onPrepareExport, onExport }, ref) {
  const [positions, setPositions] = useState(() => readIconPositions(userId));
  const [selected, setSelected] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [drag, setDrag] = useState<{ id: string; column: number; row: number } | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const suppressOpenUntil = useRef(0);
  const instructions = useId();
  const items: Shortcut[] = [...apps.map((app) => ({ id: `app:${app.id}`, title: app.title, app })), ...entries.map((entry) => ({ id: `file:${entry.name}`, title: entry.name, entry }))];
  const grid = iconGrid(workArea, density);
  const slots = resolveIconSlots(items.map((item) => item.id), positions, grid);
  const pageCount = Math.max(1, ...Object.values(slots).map((slot) => slot.page + 1));
  const activePage = Math.min(page, pageCount - 1);

  function save(next: IconPositions) { setPositions(next); writeIconPositions(userId, next); }
  function cancelGesture() {
    const current = gesture.current;
    gesture.current = null;
    setDrag(null);
    if (current?.element.hasPointerCapture?.(current.pointerId)) current.element.releasePointerCapture(current.pointerId);
  }

  useEffect(() => { cancelGesture(); }, [grid.columns, grid.rows]);

  useImperativeHandle(ref, () => ({
    organize() {
      cancelGesture();
      save(Object.fromEntries([...items].sort((a, b) => a.title.localeCompare(b.title, "pt-BR", { numeric: true })).map((item, index) => [item.id, encodeSlot(slotAt(index, grid), grid)])));
      setPage(0);
    },
    reset() { cancelGesture(); save({}); setPage(0); },
  }));

  function open(item: Shortcut) {
    if (item.app) onLaunch(item.app);
    else if (item.entry && item.entry.kind !== "blocked") onOpenEntry(item.entry);
  }

  function pointerDown(event: PointerEvent<HTMLButtonElement>, item: Shortcut) {
    if (event.button !== 0) return;
    setSelected(item.id);
    event.currentTarget.focus({ preventScroll: true });
    if (event.altKey && item.entry && ["file", "directory"].includes(item.entry.kind)) {
      setExporting(item.id);
      onPrepareExport(item.entry);
      return;
    }
    setExporting(null);
    gesture.current = { id: item.id, pointerId: event.pointerId, x: event.clientX, y: event.clientY, slot: slots[item.id], target: slots[item.id], moved: false, element: event.currentTarget };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function pointerMove(event: PointerEvent<HTMLButtonElement>) {
    const current = gesture.current;
    if (!current || event.pointerId !== current.pointerId) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (!current.moved && Math.hypot(dx, dy) < 6) return;
    current.moved = true;
    event.preventDefault();
    const column = Math.max(0, Math.min(grid.columns - 1, current.slot.column + dx / grid.stepX));
    const row = Math.max(0, Math.min(grid.rows - 1, current.slot.row + dy / grid.stepY));
    current.target = { page: current.slot.page, column: Math.round(column), row: Math.round(row) };
    setDrag({ id: current.id, column, row });
  }

  function pointerUp(event: PointerEvent<HTMLButtonElement>) {
    const current = gesture.current;
    if (!current || event.pointerId !== current.pointerId) { setExporting(null); return; }
    if (current.moved) {
      save(moveIcon(current.id, current.target, slots, grid));
      suppressOpenUntil.current = Date.now() + 350;
    }
    cancelGesture();
  }

  function keyboard(event: KeyboardEvent<HTMLButtonElement>, item: Shortcut) {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); cancelGesture(); setExporting(null); return; }
    if (event.key === "Enter") { event.preventDefault(); open(item); return; }
    if (!event.altKey || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault(); event.stopPropagation();
    const source = slots[item.id];
    save(moveIcon(item.id, { ...source, column: source.column + (event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0), row: source.row + (event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0) }, slots, grid));
    setSelected(item.id);
  }

  return <nav className="desktop-shortcuts" data-density={density} aria-label="Aplicativos da área de trabalho">
    <p id={instructions} className="desktop-icon-instructions">Clique para selecionar; duplo clique ou Enter para abrir. Arraste para organizar, ou use Alt e setas. Para baixar um arquivo, segure Alt e arraste para fora.</p>
    {items.filter((item) => slots[item.id].page === activePage).map((item) => {
      const slot = slots[item.id];
      const current = drag?.id === item.id ? drag : slot;
      return <button
        key={item.id} type="button" data-shortcut-id={item.id}
        className={`desktop-shortcut${item.entry ? " desktop-file-shortcut" : ""}${drag?.id === item.id ? " is-dragging" : ""}`}
        style={{ left: grid.padding + current.column * grid.stepX, top: grid.padding + current.row * grid.stepY, width: grid.iconWidth, height: grid.iconHeight }}
        aria-label={`Abrir ${item.title}`} aria-pressed={selected === item.id} aria-describedby={instructions}
        title={`${item.title}\nDuplo clique para abrir · Arraste para organizar${item.entry ? " · Alt + arrastar para baixar" : ""}`}
        disabled={item.entry?.kind === "blocked"} draggable={exporting === item.id}
        onClick={() => setSelected(item.id)} onDoubleClick={() => { if (Date.now() >= suppressOpenUntil.current) open(item); }}
        onPointerDown={(event) => pointerDown(event, item)} onPointerMove={pointerMove} onPointerUp={pointerUp}
        onPointerCancel={cancelGesture} onLostPointerCapture={() => { if (gesture.current) cancelGesture(); }}
        onKeyDown={(event) => keyboard(event, item)}
        onDragStart={(event) => { if (exporting !== item.id || !item.entry) event.preventDefault(); else onExport(event, item.entry); }}
        onDragEnd={() => setExporting(null)}
      >
        <span className="desktop-shortcut-symbol">{item.app ? <DesktopAppIcon app={item.app} /> : item.entry?.kind === "directory" ? <Folder size={29} aria-hidden="true" /> : <File size={27} aria-hidden="true" />}</span>
        <strong>{item.title}</strong>
      </button>;
    })}
    <div className="desktop-icons-footer">
      {pageCount > 1 && <div className="desktop-icon-pages" aria-label="Páginas de ícones">
        <button type="button" aria-label="Página anterior de ícones" disabled={activePage === 0} onClick={() => { cancelGesture(); setPage(activePage - 1); }}><ChevronLeft size={16} /></button>
        <span aria-live="polite">Página {activePage + 1} de {pageCount}</span>
        <button type="button" aria-label="Próxima página de ícones" disabled={activePage === pageCount - 1} onClick={() => { cancelGesture(); setPage(activePage + 1); }}><ChevronRight size={16} /></button>
      </div>}
    </div>
  </nav>;
});
